import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import DocumentRef from "./DocumentRef";
import SalesLeadOutcome from "./SalesLeadOutcome";
import { deepFreeze } from "../util/misc";
import COLORS from "../colors";
import { every, get } from "lodash";

export const SALES_LEAD_STATUS_FIELDS = {
  qualified: {
    assignedTo: null,
    teamID: null, // Sales team ID if assigned to a team
    drawings: [],
    outcome: { type: null },
  },
  referred: {
    referredTo: null,
    referredDate: null,
  },
  discarded: {
    reason: "",
  },
};
deepFreeze(SALES_LEAD_STATUS_FIELDS);

export const SALES_LEAD_STATUS_TYPES = {
  open: {
    order: 1,
    label: "Open",
    icon: "open",
    color: "warning",
  },
  qualified: {
    order: 2,
    label: "Qualified",
    icon: "qualified",
    color: "positive",
  },
  referred: {
    order: 6,
    label: "Referred",
    icon: "referred",
    color: "info",
  },
  discarded: {
    order: 7,
    label: "Discarded",
    icon: "delete",
    color: "negative",
  },
};
deepFreeze(SALES_LEAD_STATUS_TYPES);

export default class SalesLeadStatus extends DataStructure {
  constructor(data = {}) {
    data = SalesLeadStatus.sanitize(data);

    super(data);

    // Documents
    if (data.drawings) {
      this.data.drawings = this.drawings.map((d) => d.data);
    }

    // Outcome
    if (data.outcome && data.outcome.type) {
      this.data.outcome = this.outcome.data;
    } else if (data.type === "qualified") {
      this.data.outcome = { type: null };
    }
  }

  // Editing

  get rules() {
    return {
      assignedTo: [
        (a) => this.data.type !== "qualified" || Boolean(a) || "required",
      ],
      referredTo: [
        (a) => this.data.type !== "referred" || Boolean(a) || "required",
      ],
      referredDate: [
        (a) => this.data.type !== "referred" || Boolean(a) || "required",
      ],
      reason: [
        (a) => this.data.type !== "discarded" || Boolean(a) || "required",
      ],
    };
  }

  get isValid() {
    return Boolean(
      every(this.rules, (rules, key) =>
        rules.every((rule) => rule(get(this.data, key)) === true)
      ) &&
        (!this.data.outcome ||
          this.data.outcome.type === null ||
          this.outcome.isValid)
    );
  }

  static cleanDocuments(data, removeUnsaved = false) {
    if (data.drawings) {
      data.drawings = data.drawings.filter(
        (d) => new DocumentRef(d)[removeUnsaved ? "isSaved" : "isValid"]
      );
    }
    if (data.outcome && data.outcome.type) {
      SalesLeadOutcome.cleanDocuments(data.outcome, removeUnsaved);
    }
    return data;
  }

  static sanitize(data) {
    data = sanitize(data, {
      type: data.type,
      ...SALES_LEAD_STATUS_FIELDS[data.type],
    });
    if (data.outcome) {
      data.outcome = SalesLeadOutcome.sanitize(data.outcome);
    }
    return data;
  }

  // Formatting

  get type() {
    const type = SALES_LEAD_STATUS_TYPES[this.data.type];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get outcome() {
    return this.data.outcome && this.data.outcome.type
      ? new SalesLeadOutcome(this.data.outcome)
      : null;
  }

  get drawings() {
    return this.data.drawings
      ? this.data.drawings.map((d) => new DocumentRef(d))
      : undefined;
  }

  get assignedTo() {
    return this.data.assignedTo
      ? store.state.main.users[this.data.assignedTo]
      : undefined;
  }

  get team() {
    return this.data.teamID
      ? (store.state.main.salesTeams || {})[this.data.teamID] || null
      : null;
  }

  get referredTo() {
    return this.data.referredTo
      ? store.state.main.contacts[this.data.referredTo]
      : undefined;
  }
}
